import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormErrors } from "@/components/forms/form-errors";
import {
  ServiceEditor,
  DiscountField,
  TotalsSummary,
  decimal,
  linePayload,
  changedServices,
  serviceCurrency,
  type EditorLine,
} from "@/components/commercial/service-editor";
import { locationJobPath, locationJobBillingReviewsPath } from "@/routes";
import { money } from "../jobs/types";
type Line = {
  line_id?: string;
  line_revision?: string;
  offering_id?: string;
  offering_revision?: string;
  service_revision?: string;
  name: string;
  unit_price_minor: number;
  quantity: number;
  discount_minor?: number;
  discount_basis_points?: number;
  tax_mode?: string;
  tax_percentage?: string;
  tax_rate?: string;
  currency?: string;
};
type InputValues = {
  services?: (Partial<Line> & {
    unit_price?: string;
    discount_amount?: string;
    discount_percentage?: string;
  })[];
  billing_details?: Record<string, string>;
  advance_amount?: string;
  discount_amount?: string;
  discount_percentage?: string;
};
export default function BillingForm({
  job,
  lines,
  offerings,
  input,
}: {
  job: {
    id: string;
    revision: string;
    received_minor: string;
    currency: string;
  };
  lines: Line[];
  offerings: Line[];
  input: InputValues;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  const toLine = (line: Line): EditorLine => ({
    ...line,
    id: line.line_id || line.offering_id!,
    quantity: String(line.quantity),
    unit_price: decimal(line.unit_price_minor),
    discount_amount: decimal(line.discount_minor || 0),
    discount_percentage: decimal(line.discount_basis_points || 0),
    tax_rate: line.tax_percentage || line.tax_rate || "0",
    currency: job.currency,
  });
  const options = [...lines, ...offerings].map(toLine);
  const selected = input.services
    ? input.services.flatMap((line) => {
        const option = options.find((value) =>
          line.line_id
            ? value.line_id === line.line_id
            : !value.line_id && value.offering_id === line.offering_id,
        );
        return option
          ? [
              {
                ...option,
                ...line,
                quantity: String(line.quantity ?? option.quantity),
                unit_price: line.unit_price ?? option.unit_price,
                discount_amount: line.discount_amount ?? option.discount_amount,
                discount_percentage:
                  line.discount_percentage ?? option.discount_percentage,
              },
            ]
          : [
              {
                id: line.offering_id || line.line_id || "missing",
                name: t("workflow.unavailable_service"),
                quantity: String(line.quantity ?? 1),
                unit_price: line.unit_price ?? "0",
                discount_amount: line.discount_amount ?? "0",
                discount_percentage: line.discount_percentage ?? "0",
                offering_id: line.offering_id,
                offering_revision: line.offering_revision,
                service_revision: line.service_revision,
              },
            ];
      })
    : lines.map(toLine);
  const form = useForm(`Billing:${job.id}:${job.revision}`, {
    revision: job.revision,
    billing_review: {
      services: selected,
      discount_amount: input.discount_amount || "0",
      discount_percentage: input.discount_percentage || "0",
      advance_amount: input.advance_amount || "0",
      billing_details: {
        name: "",
        country: "",
        address: "",
        tax_identifier: "",
        ...input.billing_details,
      },
    },
  });
  if (!auth.location) return null;
  const values = form.data.billing_review,
    location = auth.location.id;
  const set = (patch: Partial<typeof values>) =>
    form.setData("billing_review", { ...values, ...patch });
  return (
    <JobWorkspace primaryAction={false}>
      <Head title={t("billing_reviews.prepare")} />
      <PageCanvas padded={false}>
        <h2 className="text-xl font-semibold">
          {t("billing_reviews.prepare")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("billing_reviews.prepare_help")}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.transform((data) => ({
              ...data,
              billing_review: {
                ...data.billing_review,
                services: data.billing_review.services.map(linePayload),
              },
            }));
            form.post(locationJobBillingReviewsPath(location, job.id));
          }}
        >
          <FormErrors
            errors={form.errors}
            targets={{
              quantity:
                values.services.length === 1
                  ? "billing-quantity-0"
                  : "billing-services",
              unit_price:
                values.services.length === 1
                  ? "billing-price-0"
                  : "billing-services",
              unit_price_minor:
                values.services.length === 1
                  ? "billing-price-0"
                  : "billing-services",
              billing_details: "billing-name",
              advance_minor: "advance",
              services: "billing-services",
            }}
          />
          {form.data.revision !== job.revision && (
            <section className="space-y-3 rounded-lg border p-4">
              <p role="alert">{t("billing_reviews.stale")}</p>
              <p className="text-sm">{t("workflow.review_context_help")}</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.setData("revision", job.revision);
                  form.clearErrors();
                }}
              >
                {t("workflow.review_context")}
              </Button>
            </section>
          )}
          <div className="mt-5 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 space-y-6">
              <ServiceEditor
                errors={form.errors}
                idPrefix="billing"
                lines={values.services}
                options={options}
                onChange={(services) => set({ services })}
              />
              <fieldset className="space-y-4 rounded-lg border p-4">
                <legend className="px-1 font-semibold">
                  {t("billing_reviews.billing_details")}
                </legend>
                <p className="text-sm text-muted-foreground">
                  {t("billing_reviews.details_help")}
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {(
                    ["name", "country", "address", "tax_identifier"] as const
                  ).map((key) => (
                    <div key={key} className="space-y-2">
                      <Label htmlFor={`billing-${key}`}>
                        {t(`billing_reviews.${key}`)}
                      </Label>
                      <Input
                        id={`billing-${key}`}
                        value={values.billing_details[key]}
                        onChange={(event) =>
                          set({
                            billing_details: {
                              ...values.billing_details,
                              [key]: event.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
            <TotalsSummary
              lines={values.services}
              amount={values.discount_amount}
              percentage={values.discount_percentage}
            >
              <DiscountField
                id="job-discount"
                amount={values.discount_amount}
                percentage={values.discount_percentage}
                onChange={(amount, percentage) =>
                  set({
                    discount_amount: amount,
                    discount_percentage: percentage,
                  })
                }
              />
              <p className="text-sm">
                {t("billing_reviews.received", {
                  amount: money(job.received_minor, job.currency, i18n.locale),
                })}
              </p>
              <div className="space-y-2">
                <Label htmlFor="advance">{t("billing_reviews.advance")}</Label>
                <Input
                  id="advance"
                  inputMode="decimal"
                  value={values.advance_amount}
                  onChange={(event) =>
                    set({ advance_amount: event.target.value })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  {t("billing_reviews.advance_help")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="submit"
                  disabled={
                    form.processing ||
                    form.data.revision !== job.revision ||
                    !serviceCurrency(values.services) ||
                    changedServices(values.services, options)
                  }
                >
                  {t("billing_reviews.preview")}
                </Button>
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={locationJobPath(location, job.id, {
                        section: "billing",
                      })}
                    />
                  }
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </TotalsSummary>
          </div>
        </form>
      </PageCanvas>
    </JobWorkspace>
  );
}
